import type { GQL } from '@aline/graphql';

import type { MentionType } from '../util/parseMarkdownMentions';

/**
 * Mention types whose URL is the plain `mention:<type>/<id>` form. The two
 * selection forms are special-cased below because they carry the encoded
 * selection context in a `?ctx=` query parameter.
 */
export type SimpleMentionType = Exclude<MentionType, 'selection' | 'selection-v2' | 'selection-v3'>;

type MentionUrlFor<TType extends SimpleMentionType> = `mention:${TType}/${string}`;

// Example: mention:document/doc-123
export type MentionUrlDocument = MentionUrlFor<'document'>;
// Example: mention:email/person@example.com
export type MentionUrlEmail = MentionUrlFor<'email'>;
// Example: mention:intake-form/current
export type MentionUrlIntakeForm = MentionUrlFor<'intake-form'>;
// Example: mention:selection/doc-123?ctx=c|0|c|Hello
export type MentionUrlSelection = `mention:selection/${string}?ctx=${string}`;
// Example: mention:selection-v2/doc-123?ctx=<base64-encoded-ExtractedContextV2>
export type MentionUrlSelectionV2 = `mention:selection-v2/${string}?ctx=${string}`;
// Example: mention:selection-v3/doc-123?ctx=<base64-encoded-SelectionContextV3>
export type MentionUrlSelectionV3 = `mention:selection-v3/${string}?ctx=${string}`;
// Example: mention:party/party-123
export type MentionUrlParty = MentionUrlFor<'party'>;
// Example: mention:task/task-123
export type MentionUrlTask = MentionUrlFor<'task'>;
// Example: mention:workflow/wf-123
export type MentionUrlWorkflow = MentionUrlFor<'workflow'>;
// Example: mention:report/report-123
export type MentionUrlReport = MentionUrlFor<'report'>;
// Example: mention:user/user-123
export type MentionUrlUser = MentionUrlFor<'user'>;
// Example: mention:playbook/playbook-123
export type MentionUrlPlaybook = MentionUrlFor<'playbook'>;
// Example: mention:aline-ai
export type MentionUrlAlineAi = 'mention:aline-ai';

/**
 * Every valid mention URL, derived from MENTION_TYPES (see
 * `util/parseMarkdownMentions.ts`): the simple types expand through the
 * template literal, the two selection forms keep their `?ctx=` suffix, and
 * Aline AI is a reserved trigger mention with no id segment.
 */
export type MentionUrl =
  | MentionUrlAlineAi
  | MentionUrlFor<SimpleMentionType>
  | MentionUrlSelection
  | MentionUrlSelectionV2
  | MentionUrlSelectionV3;

export type MarkdownMention<TMentionUrl extends MentionUrl = MentionUrl> =
  `[@${string}](${TMentionUrl})`;

/**
 * Builds the canonical mention URL for a simple (non-selection) mention type.
 * Selection URLs carry an encoded `?ctx=` payload and are constructed by the
 * selection serializers instead.
 */
export function createMentionUrl<TType extends SimpleMentionType>(
  type: TType,
  id: string
): MentionUrlFor<TType> {
  return `mention:${type}/${id}`;
}

/**
 * Report-column mentions are report-specific (used only in AI Reminder Automation prompts)
 * Example: mention:report-column/col-123
 */
export type AiColumnId = string;
export type ReportColumnId = GQL.ReportColumnFileField | AiColumnId;
export type MentionUrlReportColumn = `mention:report-column/${ReportColumnId}`;
